import React from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  X,
} from "lucide-react";
import { toUserError } from "../../utill/toUserError";
import "./notifications.scss";

// Modern inline alert with severity-coded icon + colour and an optional dismiss.
//
// Public props (backward-compatible with every existing caller):
//   error        — raw error from a `.catch(e)` or a backend payload (the same shape
//                  toUserError understands). Required for the error path.
//   variant      — "info" | "success" | "warning" | "error". Auto-derived from
//                  `error.status` when omitted: 2xx → success, 4xx/5xx → error.
//   title        — optional bold lead-in. Defaults to the severity word.
//   dismissible  — show an "x" button (default true). Hides locally on click.
//   onDismiss    — optional callback when the user clicks the dismiss button.
//
// Accessibility: the alert announces via `role="alert"` and `aria-live="polite"` so
// screen-reader users hear new errors without losing keyboard focus.
//
// Note: error code + reference id are intentionally NOT shown in the UI. They still
// flow through the API in the response envelope and end up in the server logs (keyed
// by `referenceId` MDC), which is where support actually looks.

const VARIANT_FROM_STATUS = (status) => {
  if (typeof status !== "number") return "error";
  if (status >= 200 && status < 300) return "success";
  if (status >= 400 && status < 500) return "error";
  return "error";
};

const ICONS = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

const DEFAULT_TITLES = {
  info: "Info",
  success: "Success",
  warning: "Warning",
  error: "Error",
};

class InlineAlert extends React.Component {
  state = { dismissed: false };

  handleDismiss = () => {
    this.setState({ dismissed: true });
    if (typeof this.props.onDismiss === "function") {
      this.props.onDismiss();
    }
  };

  render() {
    if (this.state.dismissed) return null;

    const safe = toUserError(this.props.error);
    const variant = this.props.variant || VARIANT_FROM_STATUS(safe.status);
    const Icon = ICONS[variant] || AlertCircle;
    const title = this.props.title || DEFAULT_TITLES[variant];
    const dismissible = this.props.dismissible !== false;

    return (
      <div
        className={`notification-alert notification-alert--${variant}`}
        role="alert"
        aria-live="polite"
      >
        <Icon className="notification-alert__icon" size={18} aria-hidden="true" />
        <div className="notification-alert__body">
          <p className="notification-alert__title">{title}</p>
          <p className="notification-alert__message">{safe.message}</p>
        </div>
        {dismissible && (
          <button
            type="button"
            className="notification-alert__dismiss"
            onClick={this.handleDismiss}
            aria-label="Dismiss alert"
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }
}

export default InlineAlert;
